import { useEffect, useMemo, useRef, useState } from 'react';
import type { WeeklyRatio } from '../lib/habitStats.js';
import { teinte } from '../lib/teinte.js';

export interface ChartSeries {
  id: string;
  label: string;
  color: string;
  strokeWidth?: number;
  points: Array<{ y: number | null; raw: WeeklyRatio }>;
}

export interface XLabel {
  index: number;
  label: string;
}

export interface LineChartProps {
  series: ChartSeries[];
  height: number;
  xLabels: XLabel[];
  // If set: the segment ending at this index is rendered dashed, and tooltips for it
  // get an "(in progress)" hint. Pass the last index when the window ends on the current week.
  currentWeekIdx?: number;
  ariaSummary: string;
}

// right: half of a centred "W37" label, so the last x label is not clipped.
const PAD = { top: 8, right: 16, bottom: 24, left: 36 };
const Y_GRID = [0, 0.25, 0.5, 0.75, 1];

export function LineChart({
  series,
  height,
  xLabels,
  currentWeekIdx,
  ariaSummary,
}: LineChartProps): React.ReactElement {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(400);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const dismissTimer = useRef<number | null>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(Math.floor(w));
    });
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, []);

  useEffect(
    () => () => {
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
    },
    []
  );

  const pointCount = series[0]?.points.length ?? 0;
  const innerW = Math.max(width - PAD.left - PAD.right, 1);
  const innerH = Math.max(height - PAD.top - PAD.bottom, 1);

  const xFor = (idx: number): number =>
    PAD.left + (pointCount > 1 ? (idx / (pointCount - 1)) * innerW : innerW / 2);
  const yFor = (ratio: number): number => PAD.top + (1 - ratio) * innerH;

  // Tooltip position (snap to nearest week band).
  function pickIdx(clientX: number): number | null {
    const wrapper = wrapperRef.current;
    if (!wrapper || pointCount === 0) return null;
    const rect = wrapper.getBoundingClientRect();
    const x = clientX - rect.left;
    if (pointCount === 1) return 0;
    const ratio = (x - PAD.left) / innerW;
    const idx = Math.round(ratio * (pointCount - 1));
    return Math.max(0, Math.min(pointCount - 1, idx));
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>): void {
    if (e.pointerType === 'touch') return;
    setHoverIdx(pickIdx(e.clientX));
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    const idx = pickIdx(e.clientX);
    setHoverIdx(idx);
    if (e.pointerType === 'touch') {
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
      dismissTimer.current = window.setTimeout(() => setHoverIdx(null), 3000);
    }
  }

  function onPointerLeave(e: React.PointerEvent<HTMLDivElement>): void {
    if (e.pointerType !== 'touch') setHoverIdx(null);
  }

  // Split a series into segments of consecutive non-null points so gaps render as gaps.
  const segmentsBySeries = useMemo(() => {
    return series.map((s) => {
      const segments: Array<Array<{ idx: number; x: number; y: number }>> = [];
      let cur: Array<{ idx: number; x: number; y: number }> = [];
      s.points.forEach((p, idx) => {
        if (p.y === null) {
          if (cur.length) segments.push(cur);
          cur = [];
        } else {
          cur.push({ idx, x: xFor(idx), y: yFor(p.y) });
        }
      });
      if (cur.length) segments.push(cur);
      return segments;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, width, height]);

  return (
    <div
      ref={wrapperRef}
      className="relative w-full touch-none"
      style={{ height }}
      onPointerMove={onPointerMove}
      onPointerDown={onPointerDown}
      onPointerLeave={onPointerLeave}
    >
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={ariaSummary}
        className="block"
      >
        {Y_GRID.map((r) => (
          <g key={r}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={yFor(r)}
              y2={yFor(r)}
              stroke="var(--bordure)"
              strokeDasharray="2 2"
            />
            <text
              x={PAD.left - 6}
              y={yFor(r) + 4}
              textAnchor="end"
              fill="var(--encre-discrete)"
              fontSize={11}
            >
              {Math.round(r * 100)}%
            </text>
          </g>
        ))}

        {xLabels.map((xl) => (
          <text
            key={xl.index}
            x={xFor(xl.index)}
            y={height - 6}
            textAnchor="middle"
            fill="var(--encre-discrete)"
            fontSize={11}
          >
            {xl.label}
          </text>
        ))}

        {series.map((s, sIdx) => {
          const segs = segmentsBySeries[sIdx]!;
          const lastSegIdx = segs.length - 1;
          const sw = s.strokeWidth ?? 1.5;
          return (
            <g key={s.id}>
              {segs.map((seg, segIdx) => {
                const isLastSeg = segIdx === lastSegIdx;
                const dashLast =
                  currentWeekIdx !== undefined &&
                  isLastSeg &&
                  seg.length >= 2 &&
                  seg[seg.length - 1]!.idx === currentWeekIdx;
                const solidPoints = dashLast ? seg.slice(0, -1) : seg;
                const dashedEdge = dashLast ? seg.slice(-2) : null;
                return (
                  <g key={segIdx}>
                    {solidPoints.length >= 2 && (
                      <polyline
                        fill="none"
                        stroke={s.color}
                        strokeWidth={sw}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        points={solidPoints.map((p) => `${p.x},${p.y}`).join(' ')}
                      />
                    )}
                    {dashedEdge && (
                      <polyline
                        fill="none"
                        stroke={s.color}
                        strokeWidth={sw}
                        strokeDasharray="4 3"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        points={dashedEdge.map((p) => `${p.x},${p.y}`).join(' ')}
                      />
                    )}
                  </g>
                );
              })}
              {segs.flat().map((p) => (
                <circle key={`${s.id}-${p.idx}`} cx={p.x} cy={p.y} r={3} fill={s.color} />
              ))}
            </g>
          );
        })}

        {hoverIdx !== null && (
          <line
            x1={xFor(hoverIdx)}
            x2={xFor(hoverIdx)}
            y1={PAD.top}
            y2={height - PAD.bottom}
            stroke="var(--bordure-forte)"
            strokeWidth={1}
          />
        )}
      </svg>

      {hoverIdx !== null && <Tooltip
        series={series}
        idx={hoverIdx}
        chartWidth={width}
        x={xFor(hoverIdx)}
        isInProgress={hoverIdx === currentWeekIdx}
      />}
    </div>
  );
}

interface TooltipProps {
  series: ChartSeries[];
  idx: number;
  chartWidth: number;
  x: number;
  isInProgress: boolean;
}

function Tooltip({ series, idx, chartWidth, x, isInProgress }: TooltipProps): React.ReactElement | null {
  const visible = series.filter((s) => s.points[idx]?.y !== null);
  if (visible.length === 0) return null;
  const first = visible[0]!.points[idx]!.raw;

  // Flip to the left of the cursor when too close to the right edge.
  const flip = x > chartWidth - 140;
  const style: React.CSSProperties = {
    left: flip ? undefined : x + 12,
    right: flip ? chartWidth - x + 12 : undefined,
    top: 8,
  };

  return (
    <div
      role="tooltip"
      className="absolute z-10 bg-surface-raised border border-edge rounded-md shadow-lg px-2 py-1.5 pointer-events-none min-w-[140px] text-xs"
      style={style}
    >
      <div className="text-muted mb-1">
        Week {first.weekNum}
        {isInProgress && <span className="ml-1">(in progress)</span>}
      </div>
      <ul className="space-y-0.5">
        {visible.map((s) => {
          const p = s.points[idx]!.raw;
          const pct = p.ratio === null ? '—' : `${Math.round(p.ratio * 100)}%`;
          return (
            <li key={s.id} className="flex items-center gap-1.5">
              <span className="point" style={teinte(s.color)} />
              <span className="truncate flex-1">{s.label}</span>
              <span className="chiffre font-normal text-muted">
                {p.count}/{p.possible} · {pct}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
